import { useEffect, useRef, useState } from "react";

interface UnicornScene {
  destroy: () => void;
  resize?: () => void;
}

interface UnicornSdk {
  addScene: (options: Record<string, unknown>) => Promise<UnicornScene>;
}

declare global {
  interface Window {
    UnicornStudio?: UnicornSdk;
  }
}

const SDK_URL = "https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v2.2.13/dist/unicornStudio.umd.js";

function loadSdk(): Promise<UnicornSdk> {
  if (window.UnicornStudio) return Promise.resolve(window.UnicornStudio);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_URL}"]`);
    const script = existing ?? document.createElement("script");
    const ready = () => window.UnicornStudio ? resolve(window.UnicornStudio) : reject(new Error("Unicorn Studio SDK 未就绪"));
    script.addEventListener("load", ready, { once: true });
    script.addEventListener("error", () => reject(new Error("Unicorn Studio SDK 加载失败")), { once: true });
    if (!existing) {
      script.src = SDK_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

export function UnicornHero() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const projectId = import.meta.env.VITE_UNICORN_PROJECT_ID?.trim();

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!projectId || projectId === "YOUR_UNICORN_PROJECT_ID" || reducedMotion) return;

    let disposed = false;
    let scene: UnicornScene | undefined;
    loadSdk()
      .then((sdk) => sdk.addScene({
        element: host,
        projectId,
        production: true,
        lazyLoad: false,
        scale: window.innerWidth < 680 ? 0.55 : 0.78,
        dpi: window.innerWidth < 680 ? 1 : 1.25,
        fps: window.innerWidth < 680 ? 30 : 45,
        altText: "流动的彩色光影背景",
        ariaLabel: "Unicorn Studio 交互式视觉场景",
        initialVariables: {
          accentColor: "#ff6b35",
          secondaryColor: "#6a5cff",
          intensity: 0.72,
        },
        interactivity: { mouse: { disableMobile: false, disabled: false } },
        breakpoints: [
          { name: "Desktop", min: 992, max: Infinity },
          { name: "Tablet", min: 576, max: 991 },
          { name: "Mobile", min: 0, max: 575 },
        ],
      }))
      .then((created) => {
        if (disposed) created.destroy();
        else {
          scene = created;
          setActive(true);
        }
      })
      .catch(() => setActive(false));

    return () => {
      disposed = true;
      scene?.destroy();
    };
  }, [projectId]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const handlePointer = (event: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      host.style.setProperty("--pointer-x", `${((event.clientX - rect.left) / rect.width) * 100}%`);
      host.style.setProperty("--pointer-y", `${((event.clientY - rect.top) / rect.height) * 100}%`);
    };
    host.addEventListener("pointermove", handlePointer);
    return () => host.removeEventListener("pointermove", handlePointer);
  }, []);

  return (
    <div ref={hostRef} id="unicorn-hero" className={`unicorn-host ${active ? "is-active" : ""}`} aria-hidden="true">
      <div className="aurora-fallback" />
      <div className="grain" />
    </div>
  );
}
